'use client'

import React, { ReactNode } from 'react'
import Link from 'next/link'
import Image from "next/image"
import type { SvgPath, CardItem } from '@/types/card-item.type'

type CardProps = {
  id: string
  ariaLabel: string
  content: CardItem[]
  layout?: 'grid' | 'flex'
  cardGrid?: 1 | 2 | 3 | 4 | 5 | 6
  wrapperClassName?: string
  cardClassName?: string
}

const defaultIcon: SvgPath[] = [
  { d: "M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" }
]

const renderSvgPaths = (icon: SvgPath[] | null | undefined): React.ReactNode => {
  if (!icon) return null
  return icon.map((p, i) => (
    <path key={i} d={p.d} strokeLinecap="round" strokeLinejoin="round" />
  ))
}

const getGridColsClass = (cols: number): string => {
  const map: Record<number, string> = {
    1: 'grid-cols-1',
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-2 md:grid-cols-3',
    4: 'sm:grid-cols-2 md:grid-cols-4',
    5: 'sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5',
    6: 'sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6'
  }
  return `grid ${map[cols] || 'grid-cols-1'}`
}

const Card = ({
  id,
  ariaLabel,
  content,
  layout = 'grid',
  cardGrid = 1,
  wrapperClassName = '',
  cardClassName = ''
}: CardProps) => {
  const cardId = id.replace(/\s+/g, '')

  const layoutClass =
    layout === 'grid'
      ? `${getGridColsClass(cardGrid)}`
      : 'flex flex-wrap'

  return (
    <div
      id={cardId}
      aria-label={ariaLabel}
      className={`${layoutClass} gap-4 sm:gap-8 ${wrapperClassName}`}

    >
      {content.map((item, index) => {
        const iconPaths = item.icon === null ? null : item.icon ?? defaultIcon
        const buildAlt = (title?: string) => {
          const base = (title || ariaLabel).trim()
          return `${base} — NRD REAL ESTATE (Philadelphia)`
        }

        const CardContent = (
          <>
            {item.imageSrc && (
              <figure key={index} className="outline outline-offset-2 outline-4 outline-[var(--border-color)] hover:outline-[var(--primary-color)] rounded-xl overflow-hidden mb-12">
                <Image
                  className="thumbnail object-cover grayscale hover:grayscale-0 w-[100%] h-[200px] md:w-[100%] md:h-[250px]"
                  width={350}
                  height={200}
                  src={item.imageSrc}
                  alt={buildAlt(item.title)}
                  sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  quality={70}
                />
              </figure>
            )}
            {iconPaths && (
              <svg
                className="flex-shrink-0 w-16 h-16 mb-6"
                fill="none"
                stroke="url(#grad1)"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" style={{ stopColor: "#9333ea", stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: "#3b82f6", stopOpacity: 1 }} />
                  </linearGradient>
                </defs>
                {renderSvgPaths(iconPaths)}
              </svg>
            )}
            <h2 className="text-xl font-semibold mb-1">{item.title}</h2>
            {item.subTitle && (
              <p className="font-medium text-purple-400 mb-2">{item.subTitle}</p>
            )}
            {item.description && (
              <p className="text-[var(--body-1-color)] text-lg mb-4">{item.description}</p>
            )}
            {item.footer && (
              <div className="pt-4 border-t border-[var(--border-color)]">
                {item.footer}
              </div>
            )}
          </>
        )

        const baseCardClass = `flex flex-col ${cardClassName}`

        return item.href ? (
          <Link key={index} href={item.href} className={baseCardClass} title={`${item.title} — NRD REAL ESTATE`}>
            {CardContent}
          </Link>
        ) : (
          <div key={index} className={baseCardClass}>
            {CardContent}
          </div>
        )
      })}
    </div>
  )
}

export default Card
